import React from 'react';
import { GameSettings } from '../SettingsModal';
import usePageTranslation from '../../../../../shared/hooks/usePageTranslation';

interface InterfaceSettingsTabProps {
  settings: GameSettings;
  saveSettings: (updates: Partial<GameSettings>) => void;
}

const InterfaceSettingsTab: React.FC<InterfaceSettingsTabProps> = ({ settings, saveSettings }) => {
  const { t } = usePageTranslation('game', 'SettingsModal');
  
  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-xl font-semibold text-white mb-4">{t('interface.appearance.title')}</h3>
        
        <div className="space-y-4">
          <div>
            <label className="block text-gray-300 font-medium mb-2">{t('interface.appearance.language.label')}</label>
            <select
              value={settings.language}
              onChange={(e) => saveSettings({ language: e.target.value })}
              className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              <option value="fr">{t('interface.appearance.language.fr')}</option>
              <option value="en">{t('interface.appearance.language.en')}</option>
            </select>
          </div>

          <div>
            <label className="block text-gray-300 font-medium mb-2">{t('interface.appearance.theme.label')}</label>
            <div className="grid grid-cols-3 gap-3">
              {(['dark', 'light', 'auto'] as const).map((theme) => (
                <button
                  key={theme}
                  onClick={() => saveSettings({ theme })}
                  className={`px-4 py-3 rounded-lg border-2 transition-all ${
                    settings.theme === theme
                      ? 'border-indigo-500 bg-indigo-500/20 text-indigo-400'
                      : 'border-gray-700 bg-gray-800/50 text-gray-300 hover:border-gray-600'
                  }`}
                >
                  <div className="font-semibold capitalize">{theme}</div>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="space-y-4">
        <h3 className="text-xl font-semibold text-white">{t('interface.display.title')}</h3>
        
        <div className="bg-gray-800/50 rounded-lg p-4 space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-300 font-medium">{t('interface.display.showTooltips.label')}</p>
              <p className="text-sm text-gray-400">{t('interface.display.showTooltips.description')}</p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={settings.showTooltips}
                onChange={(e) => saveSettings({ showTooltips: e.target.checked })}
                className="sr-only peer"
              />
              <div className="w-14 h-7 bg-gray-600 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-indigo-800 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-[4px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-6 after:w-6 after:transition-all peer-checked:bg-indigo-600"></div>
            </label>
          </div>

          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-300 font-medium">{t('interface.display.compactMode.label')}</p>
              <p className="text-sm text-gray-400">{t('interface.display.compactMode.description')}</p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={settings.compactMode}
                onChange={(e) => saveSettings({ compactMode: e.target.checked })}
                className="sr-only peer"
              />
              <div className="w-14 h-7 bg-gray-600 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-indigo-800 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-[4px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-6 after:w-6 after:transition-all peer-checked:bg-indigo-600"></div>
            </label>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InterfaceSettingsTab;

